import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import {
  HubListGroup,
  HubRow,
  HubScreen,
  HubSegment,
  HubSheet,
} from "../AdminPortal/HubPwa";
import TrainerSchedulePwa from "./TrainerSchedulePwa";
import TrainerProfileHero from "./TrainerProfileHero";
import { resolveTrainerPwaTab, TRAINER_PWA_TABS } from "./doctorConstants";
import aa from "../AdminPortal/adminApp.module.css";
import { EmptyState } from "../../ui";

const TrainerCalendarPwa = lazy(() => import("./TrainerCalendarPwa"));
const TrainerAssignmentsPwa = lazy(() => import("./TrainerAssignmentsPwa"));
const TrainerSettingsPwa = lazy(() => import("./TrainerSettingsPwa"));
const TrainerWaitlistPwa = lazy(() => import("./TrainerWaitlistPwa"));

function TabLoading() {
  return <p className={aa.hubHint}>Загрузка…</p>;
}

function sessionCounts(sessions = []) {
  const now = new Date();
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  const weekEnd = new Date(now);
  weekEnd.setDate(weekEnd.getDate() + 7);

  const active = sessions.filter((row) => row.status !== "cancelled");
  const today = active.filter((row) => {
    const t = new Date(row.start_datetime);
    return t >= startOfDay && t <= endOfDay;
  }).length;
  const week = active.filter((row) => {
    const t = new Date(row.start_datetime);
    return t >= now && t <= weekEnd;
  }).length;
  const upcoming = active
    .filter((row) => new Date(row.start_datetime).getTime() >= now.getTime())
    .sort(
      (a, b) =>
        new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime(),
    );

  return { today, week, upcoming };
}

function sessionTitle(session) {
  if (session.title?.trim()) return session.title;
  if (session.group?.name) return session.group.name;
  if (session.specialization?.name) return session.specialization.name;
  return session.session_kind === "event" ? "Событие" : "Занятие";
}

export default function TrainerProfilePwa({
  doctorId,
  doctor,
  edit,
  setEdit,
  branches,
  directionCatalog,
  groupCatalog,
  workload,
  summary,
  loading,
  newPassword,
  setNewPassword,
  onSaveDoctor,
  onSaveBranches,
  onSetPassword,
  onDeactivate,
  onReload,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const tab = resolveTrainerPwaTab(tabFromUrl);

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [assignmentsSaving, setAssignmentsSaving] = useState(false);

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "overview") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!doctorId) return undefined;
    let cancelled = false;
    setSessionsLoading(true);
    const from = new Date();
    const to = new Date(from);
    to.setDate(to.getDate() + 60);
    clinicAPI
      .getTrainingSessions({
        doctor_id: doctorId,
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
      })
      .then((res) => {
        if (!cancelled) setSessions(res.sessions || []);
      })
      .catch(() => {
        if (!cancelled) setSessions([]);
      })
      .finally(() => {
        if (!cancelled) setSessionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doctorId]);

  const { today, week, upcoming } = useMemo(() => sessionCounts(sessions), [sessions]);

  const openClubCalendar = () => {
    navigate(`/manage/app/schedule?trainer=${doctorId}&view=week`);
  };

  const openSession = (session) => {
    const day = session.start_datetime?.slice(0, 10);
    navigate(
      `/manage/app/schedule?trainer=${doctorId}&view=day${day ? `&date=${day}` : ""}`,
    );
  };

  const kpiItems = [
    { key: "today", label: "Сегодня", value: String(today), hint: "занятий" },
    { key: "week", label: "Неделя", value: String(week), hint: "впереди" },
    {
      key: "schedule",
      label: "Шаблон",
      value: summary?.has_schedule ? "Есть" : "Нет",
      hint: "расписание",
    },
    {
      key: "hours",
      label: workload?.month?.slice(5) || "Месяц",
      value: workload ? String(workload.total_hours ?? 0) : "—",
      hint: "часов",
    },
  ];

  const overviewContent = (
    <>
      <div className={aa.trainerKpiGrid}>
        {kpiItems.map((item) => (
          <article key={item.key} className={aa.trainerKpiCard}>
            <span className={aa.trainerKpiValue}>{item.value}</span>
            <span className={aa.trainerKpiLabel}>{item.label}</span>
            <span className={aa.trainerKpiHint}>{item.hint}</span>
          </article>
        ))}
      </div>

      {upcoming[0] ? (
        <button
          type="button"
          className={aa.trainerNextSession}
          onClick={() => openSession(upcoming[0])}
        >
          <span className={aa.trainerNextSessionLabel}>Ближайшее занятие</span>
          <strong className={aa.trainerNextSessionTitle}>
            {sessionTitle(upcoming[0])}
          </strong>
          <span className={aa.trainerNextSessionMeta}>
            {formatDateRu(upcoming[0].start_datetime)} ·{" "}
            {formatTimeRu(upcoming[0].start_datetime)}
          </span>
        </button>
      ) : !sessionsLoading ? (
        <p className={aa.trainerEmptyHint}>Предстоящих занятий нет</p>
      ) : null}

      <section className={aa.trainerSection}>
        <h2 className={aa.hubSectionLabel}>Ближайшие занятия</h2>
        {sessionsLoading ? (
          <p className={aa.hubHint}>Загрузка…</p>
        ) : upcoming.length === 0 ? (
          <EmptyState title="Занятий нет" description="Создайте тренировку в календаре клуба" />
        ) : (
          <HubListGroup>
            {upcoming.slice(0, 8).map((session) => (
              <HubRow
                key={session.id}
                title={sessionTitle(session)}
                meta={formatTimeRu(session.start_datetime)}
                sub={formatDateRu(session.start_datetime)}
                badge={
                  session.session_kind === "event" ? "Событие" : undefined
                }
                badgeTone={session.session_kind === "event" ? "warn" : "accent"}
                onClick={() => openSession(session)}
              />
            ))}
          </HubListGroup>
        )}
        {upcoming.length > 8 ? (
          <button
            type="button"
            className={aa.hubSecondaryCta}
            onClick={openClubCalendar}
          >
            Все занятия в календаре
          </button>
        ) : null}
      </section>
    </>
  );

  return (
    <HubScreen
      title={doctor?.full_name || "Тренер"}
      onBack={() => navigate("/manage/app/team")}
      bodyClassName={aa.trainerProfileHubBody}
    >
      {loading && !doctor ? (
        <p className={aa.hubHint}>Загрузка…</p>
      ) : (
        <>
          <TrainerProfileHero
            doctor={doctor}
            onOpenCalendar={openClubCalendar}
            onOpenSchedule={() => setTab("schedule")}
            onOpenActions={() => setActionsOpen(true)}
          />

          <HubSheet
            open={actionsOpen}
            onClose={() => setActionsOpen(false)}
            title="Действия"
            subtitle={doctor?.full_name || undefined}
          >
            <div className={aa.trainerActionsSheet}>
              <button
                type="button"
                className={aa.hubSecondaryCta}
                onClick={() => {
                  setActionsOpen(false);
                  setTab("settings");
                }}
              >
                Редактировать профиль
              </button>
              <button
                type="button"
                className={aa.hubSecondaryCta}
                onClick={() => {
                  setActionsOpen(false);
                  setTab("assignments");
                }}
              >
                Назначения и залы
              </button>
              {doctor?.email ? (
                <a
                  href={`mailto:${doctor.email}`}
                  className={aa.trainerActionsSheetLink}
                  onClick={() => setActionsOpen(false)}
                >
                  Написать на email
                </a>
              ) : null}
              {doctor?.is_active !== false ? (
                <button
                  type="button"
                  className={aa.trainerDangerCta}
                  onClick={() => {
                    setActionsOpen(false);
                    onDeactivate();
                  }}
                >
                  Деактивировать тренера
                </button>
              ) : null}
            </div>
          </HubSheet>

          <div className={aa.hubSegmentWrap}>
            <HubSegment tabs={TRAINER_PWA_TABS} active={tab} onChange={setTab} />
          </div>

          {tab === "overview" ? overviewContent : null}

          {tab === "calendar" ? (
            <Suspense fallback={<TabLoading />}>
              <TrainerCalendarPwa
                doctorId={doctorId}
                doctorBranchOptions={branches}
              />
            </Suspense>
          ) : null}

          {tab === "schedule" ? (
            <TrainerSchedulePwa
              doctorId={doctorId}
              doctorBranchOptions={branches}
              onScheduleSaved={onReload}
            />
          ) : null}

          {tab === "assignments" ? (
            <Suspense fallback={<TabLoading />}>
              <TrainerAssignmentsPwa
                branches={branches}
                branchIds={edit.branchIds}
                onToggleBranch={(id) =>
                  setEdit((prev) => ({
                    ...prev,
                    branchIds: prev.branchIds.includes(id)
                      ? prev.branchIds.filter((x) => x !== id)
                      : [...prev.branchIds, id],
                  }))
                }
                directions={directionCatalog}
                groups={groupCatalog}
                directionIds={edit.directionIds}
                groupIds={edit.groupIds}
                onChangeAssignments={(patch) =>
                  setEdit((prev) => ({ ...prev, ...patch }))
                }
                onSave={async () => {
                  setAssignmentsSaving(true);
                  try {
                    await onSaveBranches();
                  } finally {
                    setAssignmentsSaving(false);
                  }
                }}
                saving={assignmentsSaving}
              />
            </Suspense>
          ) : null}

          {tab === "waitlist" ? (
            <Suspense fallback={<TabLoading />}>
              <TrainerWaitlistPwa
                doctorId={doctorId}
                doctorBranchOptions={branches}
              />
            </Suspense>
          ) : null}

          {tab === "settings" ? (
            <Suspense fallback={<TabLoading />}>
              <TrainerSettingsPwa
                doctorId={doctorId}
                doctor={doctor}
                edit={edit}
                setEdit={setEdit}
                onSaveDoctor={onSaveDoctor}
                onPhotoUploaded={onReload}
                newPassword={newPassword}
                setNewPassword={setNewPassword}
                onSetPassword={onSetPassword}
                onDeactivate={onDeactivate}
              />
            </Suspense>
          ) : null}
        </>
      )}
    </HubScreen>
  );
}
